import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { AuthContextType, AuthUser, LoginRequest, LoginResponse, PendingAuth, PendingSoporteAuth, PendingTotp } from "../models/auth";
import { AuthService } from "../services/authService";
import { TokenService } from "../services/tokenService";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null);
    const [pendingTotp, setPendingTotp] = useState<PendingTotp | null>(null);
    const [pendingSoporteAuth, setPendingSoporteAuth] = useState<PendingSoporteAuth | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setIsLoading(true);
                // El access token dura poco: si venció pero hay sesión guardada, se renueva con la cookie
                if (!TokenService.isTokenValid() && TokenService.getToken() && TokenService.getUser()) {
                    await AuthService.refreshToken();
                }
                if (TokenService.isTokenValid()) {
                    const userData = TokenService.getUser();
                    if (userData) {
                        setUser(userData);
                        const isValidSession = await AuthService.validateSession();
                        if (!isValidSession) {
                            setUser(null);
                            setError("Sesión expirada. Por favor, inicie sesión nuevamente.");
                        } else {
                            // Recargar usuario por si se actualizaron los nombres
                            const refreshed = TokenService.getUser();
                            if (refreshed) setUser(refreshed);
                        }
                    }
                } else {
                    TokenService.removeToken();
                }
            } catch (error) {
                console.error("Auth initialization error:", error);
                setError("Error al inicializar la autenticación");
                TokenService.removeToken();
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    useEffect(() => {
        if (!user) return;
        const checkTokenExpiration = async () => {
            if (TokenService.willTokenExpireSoon()) {
                const newToken = await AuthService.refreshToken();
                if (!newToken) {
                    setError("Sesión expirada. Por favor, inicie sesión nuevamente.");
                    setUser(null);
                }
            }
        };
        const interval = setInterval(checkTokenExpiration, 60000);
        return () => clearInterval(interval);
    }, [user]);

    /** Aplica una respuesta de login/verify-totp: paso intermedio (2FA, sucursal, empresa soporte) o sesión. */
    const aplicarRespuesta = (response: LoginResponse): void => {
        if (response.requiresTotp) {
            setPendingTotp({ totpToken: response.totpToken!, username: response.username });
        } else if (response.requiresSucursalSelection) {
            // Guardar estado de pre-autenticación: el LoginView mostrará el selector
            setPendingAuth({
                preAuthToken: response.preAuthToken!,
                sucursales: response.sucursalesDisponibles!,
                username: response.username,
            });
        } else if (response.requiresEmpresaSoporteSelection) {
            // Usuario soporte con múltiples empresas: mostrar selector de empresa
            setPendingSoporteAuth({
                preAuthToken: response.preAuthToken!,
                empresas: response.empresasSoporteDisponibles!,
                username: response.username,
            });
        } else {
            const userData: AuthUser = {
                username: response.username,
                empresaId: response.empresaId!,
                sucursalId: response.sucursalId,
                sucursalNombre: response.sucursalNombre,
                empresaNombre: response.empresaNombre,
                isAuthenticated: true,
                esSoporte: response.esSoporte ?? false,
                soporteEscritura: response.soporteEscritura ?? false,
                requiereCambioPassword: response.requiereCambioPassword ?? false,
            };
            setUser(userData);
        }
    };

    const login = async (credentials: LoginRequest): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            setPendingAuth(null);
            setPendingTotp(null);

            const response = await AuthService.login(credentials);
            aplicarRespuesta(response);
        } catch (error: any) {
            const errorMessage = error.message || "Error durante el login";
            setError(errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const verifyTotp = async (codigo: string, recordarDispositivo = false): Promise<void> => {
        if (!pendingTotp) throw new Error("No hay un inicio de sesión pendiente de código");
        try {
            setIsLoading(true);
            setError(null);
            const response = await AuthService.verifyTotp(pendingTotp.totpToken, codigo, recordarDispositivo);
            setPendingTotp(null);
            aplicarRespuesta(response);
        } catch (error: any) {
            setError(error.message || "Código incorrecto");
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const selectSucursal = async (sucursalId: number): Promise<void> => {
        if (!pendingAuth) throw new Error("No hay sesión pendiente de selección de sucursal");

        try {
            setIsLoading(true);
            setError(null);

            const response = await AuthService.selectSucursal(pendingAuth.preAuthToken, sucursalId);

            const userData: AuthUser = {
                username: response.username,
                empresaId: response.empresaId!,
                sucursalId: response.sucursalId,
                sucursalNombre: response.sucursalNombre,
                empresaNombre: response.empresaNombre,
                isAuthenticated: true,
                esSoporte: false,
            };

            setPendingAuth(null);
            setUser(userData);
        } catch (error: any) {
            const errorMessage = error.message || "Error al seleccionar sucursal";
            setError(errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const selectEmpresaSoporte = async (empresaId: number): Promise<void> => {
        if (!pendingSoporteAuth) throw new Error("No hay sesión de soporte pendiente");

        try {
            setIsLoading(true);
            setError(null);

            const response = await AuthService.selectEmpresaSoporte(
                pendingSoporteAuth.preAuthToken,
                empresaId
            );

            const userData: AuthUser = {
                username: response.username,
                empresaId: response.empresaId!,
                sucursalId: response.sucursalId,
                sucursalNombre: response.sucursalNombre,
                empresaNombre: response.empresaNombre,
                isAuthenticated: true,
                esSoporte: true,
                soporteEscritura: response.soporteEscritura ?? false,
            };

            setPendingSoporteAuth(null);
            setUser(userData);
        } catch (error: any) {
            const errorMessage = error.message || "Error al seleccionar empresa de soporte";
            setError(errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = (): void => {
        try {
            AuthService.logout();
            setUser(null);
            setPendingAuth(null);
            setPendingTotp(null);
            setPendingSoporteAuth(null);
            setError(null);
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    /** Se llama tras cambiar la contraseña temporal: levanta el bloqueo de la app. */
    const marcarPasswordCambiada = (): void => {
        setUser((prev) => {
            if (!prev) return prev;
            const actualizado = { ...prev, requiereCambioPassword: false };
            TokenService.setUser(actualizado);
            return actualizado;
        });
    };

    const value: AuthContextType = {
        marcarPasswordCambiada,
        user,
        pendingAuth,
        pendingSoporteAuth,
        pendingTotp,
        verifyTotp,
        login,
        selectSucursal,
        selectEmpresaSoporte,
        logout,
        isLoading,
        error,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const withAuth = <P extends object>(Component: React.ComponentType<P>) => {
    return (props: P) => {
        const { user, isLoading } = useAuth();
        if (isLoading) return <div>Cargando...</div>;
        if (!user?.isAuthenticated) return <div>No autorizado. Redirigiendo al login...</div>;
        return <Component {...props} />;
    };
};
